import { DollarSign } from 'lucide-react';
import Image from 'next/image';
import React from 'react';
import { useCurrency } from 'src/modules/currencies';
import { CurrencyIcon } from 'src/modules/currencies/components/CurrencyIcon';
import { formatUnits } from 'viem';

import { Skeleton, Text, TextProps } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';
import { EthereumAddress } from '@/utils/currency';
import { formatDecimalAmount, maskAddressValue } from '@/utils/format';

type TextVariantType = NonNullable<TextProps['variant']>;

const MUI_TO_DS_VARIANT_MAP: Record<string, TextVariantType> = {
  body1: 'body-lg',
  body2: 'body-md',
  caption: 'caption',
  subtitle2: 'body-md',
};

interface FormattedAmountProps {
  amount: string | number | bigint;
  currencyAddress?: EthereumAddress;
  chainId?: number;
  isPoints?: boolean;
  isUSD?: boolean;
  skipDecimals?: boolean;
  className?: string;
  typographyVariant?: string;
  showIcon?: boolean;
}

export const FormattedAmount = ({
  amount,
  currencyAddress,
  chainId,
  isPoints = false,
  isUSD = false,
  skipDecimals = false,
  className,
  typographyVariant = 'body2',
  showIcon = true,
}: FormattedAmountProps) => {
  const { getChainIconUrl } = useGetChainInfo();

  const isCurrencyMode = !isPoints && !isUSD;
  const { data: currency, isLoading: isCurrencyLoading } = useCurrency(
    isCurrencyMode ? currencyAddress : undefined,
    isCurrencyMode && chainId != null ? chainId.toString() : undefined,
  );

  const dsVariant = MUI_TO_DS_VARIANT_MAP[typographyVariant] ?? 'body-md';

  // Handle points formatting
  if (isPoints) {
    const pointsAmount = formatDecimalAmount(Number(amount));
    const pointsLabel = Number(amount) === 1 ? 'POINT' : 'POINTS';

    return (
      <div className={`tw:flex tw:items-center tw:gap-[0.3rem] ${className ?? ''}`}>
        {showIcon && (
          <div className="tw:w-5 tw:h-5 tw:relative">
            <Image src="/assets/svg/coin.svg" alt="points" fill />
          </div>
        )}
        <Text variant={dsVariant} weight="medium">
          {pointsAmount} {pointsLabel}
        </Text>
      </div>
    );
  }

  // Handle USD formatting
  if (isUSD) {
    const usdAmount = formatDecimalAmount(Number(amount));

    return (
      <div className={`tw:flex tw:items-center tw:gap-0 ${className ?? ''}`}>
        {showIcon && <DollarSign width={16} height={16} />}
        <Text variant={dsVariant} weight="medium">
          {usdAmount} USD
        </Text>
      </div>
    );
  }

  // For regular currency, chainId and currencyAddress are required
  if (!chainId || !currencyAddress) {
    console.warn('FormattedAmount: chainId and currencyAddress are required for regular currency formatting');
    return null;
  }

  const currencySymbol = currency?.name ?? maskAddressValue(currencyAddress);
  const currencyDecimals = currency?.decimals ?? 18;

  // Handle regular currency formatting
  const formattedAmount = skipDecimals
    ? formatDecimalAmount(Number(amount))
    : formatDecimalAmount(Number(formatUnits(BigInt(amount), currencyDecimals)));

  // Check if we found a currency in the DB (symbol will be the address if not found)
  const isCurrencyKnown = currencySymbol !== currencyAddress;

  // If currency is not known, use network logo and masked address
  const useNetworkFallback = !isCurrencyKnown;
  const networkImageSrc = useNetworkFallback ? getChainIconUrl(chainId) : null;
  const displaySymbol = useNetworkFallback ? maskAddressValue(currencyAddress) : currencySymbol;

  if (isCurrencyLoading) {
    return (
      <div className={`tw:flex tw:items-center tw:gap-[0.3rem] ${className ?? ''}`}>
        {showIcon && <Skeleton variant="circular" width={20} height={20} />}
        <Skeleton width={80} height={16} />
      </div>
    );
  }

  return (
    <div className={`tw:flex tw:items-center tw:gap-[0.3rem] ${className ?? ''}`}>
      {showIcon &&
        (useNetworkFallback && networkImageSrc ? (
          <div className="tw:w-5 tw:h-5 tw:relative">
            <Image src={networkImageSrc} alt={currencyAddress} fill />
          </div>
        ) : (
          <CurrencyIcon logoUri={currency?.logoUri} name={currencySymbol} size={20} />
        ))}
      <Text variant={dsVariant} weight="medium">
        {formattedAmount} {displaySymbol}
      </Text>
    </div>
  );
};
